import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetModerationReportsQuery, useGetModerationReportStatsQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import ActionButtons from '../../../components/ActionButtons';
import { useLanguage } from '../../../context/LanguageContext';
import { formatDateTime } from '../../../utils/helpers';

export const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'resolved': return 'badge-light-success';
    case 'reviewed': return 'badge-light-info';
    case 'dismissed': return 'badge-light-secondary text-dark';
    default: return 'badge-light-warning';
  }
};

export const actionLabels: Record<string, string> = {
  none: 'No Action',
  warn_user: 'Warning Issued',
  suspend_user: 'Account Suspended',
  reject_ad: 'Ad Removed',
};

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useGetModerationReportsQuery({
    search: search || undefined,
    type: type || undefined,
    status: status || undefined,
    page,
  });
  const { data: statsData } = useGetModerationReportStatsQuery(undefined);

  const reports = data?.data?.data || [];
  const meta = data?.data;
  const stats = statsData?.data;

  useEffect(() => {
    setPage(1);
  }, [search, type, status]);

  if (isLoading) {
    return <PageLoader />;
  }

  const statCards = [
    { key: 'pending', label: t('Pending Review'), icon: 'fa-clock', color: 'warning' },
    { key: 'reviewed', label: t('Reviewed'), icon: 'fa-eye', color: 'info' },
    { key: 'resolved', label: t('Resolved'), icon: 'fa-check-circle', color: 'success' },
    { key: 'dismissed', label: t('Dismissed'), icon: 'fa-ban', color: 'dark' },
  ];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('Reports & Complaints')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary" onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer' }}>{t('Home')}</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">{t('Reports & Complaints')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="row g-5 g-xl-8 mb-5">
              {statCards.map((card) => (
                <div key={card.key} className="col-sm-6 col-xl-3">
                  <div className={`card bg-light-${card.color} card-flush h-100`}>
                    <div className="card-body d-flex align-items-center gap-4">
                      <span className={`symbol-label bg-${card.color} text-white rounded w-50px h-50px d-flex align-items-center justify-content-center`}>
                        <i className={`fas ${card.icon} fs-2 text-white`}></i>
                      </span>
                      <div className="d-flex flex-column">
                        <span className="fs-2 fw-bold text-gray-800">{stats?.by_status?.[card.key] ?? 0}</span>
                        <span className="fs-7 text-gray-600">{card.label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card card-flush">
              <div className="card-header align-items-center py-5 gap-2 gap-md-5">
                <div className="card-title">
                  <div className="d-flex align-items-center position-relative my-1">
                    <span className="svg-icon svg-icon-1 position-absolute ms-4">
                      <i className="fas fa-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-solid w-250px ps-14"
                      placeholder={t('Search reporter, ad, reason...')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="card-toolbar flex-row-fluid justify-content-end gap-3">
                  <div className="w-100 mw-175px">
                    <select className="form-select form-select-solid" value={type} onChange={(e) => setType(e.target.value)}>
                      <option value="">{t('All Report Types')}</option>
                      <option value="ad">{t('Ad Reports')}</option>
                      <option value="user">{t('User Reports')}</option>
                    </select>
                  </div>
                  <div className="w-100 mw-150px">
                    <select className="form-select form-select-solid" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="">{t('All Status')}</option>
                      <option value="pending">{t('Pending')}</option>
                      <option value="reviewed">{t('Reviewed')}</option>
                      <option value="resolved">{t('Resolved')}</option>
                      <option value="dismissed">{t('Dismissed')}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th className="min-w-175px">{t('Reporter')}</th>
                        <th className="min-w-175px">{t('Reported')}</th>
                        <th className="min-w-125px">{t('Reason')}</th>
                        <th className="min-w-100px">{t('Type')}</th>
                        <th className="min-w-100px">{t('Status')}</th>
                        <th className="min-w-125px">{t('Action Taken')}</th>
                        <th className="min-w-125px">{t('Submitted')}</th>
                        <th className="text-end min-w-75px">{t('Actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {reports.length > 0 ? (
                        reports.map((r: any) => (
                          <tr key={`${r.type}-${r.id}`}>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="text-gray-800">{r.reporter?.name || t('Unknown User')}</span>
                                <span className="fs-8">{r.reporter?.email}</span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="text-gray-800">{r.accused?.name || t('Unknown User')}</span>
                                {r.ad && <span className="fs-8">{t('Ad')}: {r.ad.title} ({r.ad.ad_id || `#${r.ad.id}`})</span>}
                              </div>
                            </td>
                            <td>{r.reason || '—'}</td>
                            <td>
                              <span className={`badge ${r.type === 'ad' ? 'badge-light-primary' : 'badge-light-dark'}`}>
                                {r.type === 'ad' ? t('Ad Report') : t('User Report')}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${statusBadgeClass(r.status)} text-capitalize`}>{t(r.status)}</span>
                            </td>
                            <td>{r.action_taken && r.action_taken !== 'none' ? t(actionLabels[r.action_taken]) : '—'}</td>
                            <td>{formatDateTime(r.created_at)}</td>
                            <td className="text-end">
                              <ActionButtons
                                onView={() => navigate(`/admin/user-reports/${r.type}/${r.id}`)}
                                viewTitle={t('Review')}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center">{t('No reports found')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {meta && (
                  <Pagination
                    currentPage={page}
                    lastPage={meta.last_page}
                    total={meta.total}
                    perPage={meta.per_page}
                    onPageChange={setPage}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
